import React, { useState, useRef, useEffect } from "react";
import { useFindMany, useGlobalAction } from "@gadgetinc/react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../api";

const StatusBadge = ({ isActive, expiresAt, currentUses, maxUses }) => {
  const now = new Date();
  const expired = expiresAt && new Date(expiresAt) < now;
  const exhausted = maxUses && currentUses >= maxUses;
  
  const getBadgeStyle = (type) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    };
    
    switch(type) {
      case 'active':
        return { ...baseStyle, background: '#065f46', color: '#d1fae5' };
      case 'expired':
        return { ...baseStyle, background: '#7f1d1d', color: '#fed7d7' };
      case 'exhausted':
        return { ...baseStyle, background: '#92400e', color: '#fef3c7' };
      case 'inactive':
        return { ...baseStyle, background: '#374151', color: '#9ca3af' };
      default:
        return baseStyle;
    }
  };
  
  if (!isActive) {
    return <span style={getBadgeStyle('inactive')}>Inactive</span>;
  }
  if (expired) {
    return <span style={getBadgeStyle('expired')}>Expired</span>;
  }
  if (exhausted) {
    return <span style={getBadgeStyle('exhausted')}>Exhausted</span>;
  }
  return <span style={getBadgeStyle('active')}>Active</span>;
};

const AccessCodeTable = ({ accessCodes, onRefresh }) => {
  const formatDate = (dateStr) => {
    return dateStr ? new Date(dateStr).toLocaleDateString() : 'Never';
  };

  const formatUsage = (current, max) => {
    return max ? `${current}/${max}` : `${current}/∞`;
  };

  const getCodeTypeStyle = (codeType) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'capitalize'
    };
    
    switch(codeType) {
      case 'all':
        return { ...baseStyle, background: '#065f46', color: '#d1fae5' };
      case 'specific':
        return { ...baseStyle, background: '#92400e', color: '#fef3c7' };
      default:
        return { ...baseStyle, background: '#374151', color: '#9ca3af' };
    }
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Code</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Code Type</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Usage</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Duration</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Status</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Expires</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Created</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Assistant Access</th>
            <th style={{ background: '#1f2937', padding: '12px 8px', textAlign: 'left', fontWeight: 'bold', color: '#ffffff', borderBottom: '2px solid #374151' }}>Description</th>
          </tr>
        </thead>
        <tbody>
          {accessCodes.map((code) => (
            <tr key={code.id} style={{ background: '#111111', borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '14px', color: '#ffffff' }}>
                    {code.code}
                  </span>
                  <button 
                    style={{
                      background: '#1f2937',
                      border: '1px solid #374151',
                      color: '#9ca3af',
                      padding: '4px 6px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    onClick={() => navigator.clipboard.writeText(code.code)}
                    title="Copy code"
                  >
                    📋
                  </button>
                </div>
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                <span style={getCodeTypeStyle(code.codeType)}>
                  {code.codeType?.replace('_', ' ')}
                </span>
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', color: '#ffffff' }}>
                {formatUsage(code.currentUses, code.maxUses)}
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', color: '#ffffff' }}>
                {code.duration.replace('_', ' ')}
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top' }}>
                <StatusBadge 
                  isActive={code.isActive}
                  expiresAt={code.expiresAt}
                  currentUses={code.currentUses}
                  maxUses={code.maxUses}
                />
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', color: '#ffffff' }}>
                {formatDate(code.expiresAt)}
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', color: '#ffffff' }}>
                {formatDate(code.createdAt)}
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {code.assistantIds && code.assistantIds.length > 0 ? (
                  <span title={`Assistant IDs: ${code.assistantIds.join(', ')}`} style={{ color: '#ffffff' }}>
                    {code.assistantIds.length} assistant(s)
                  </span>
                ) : (
                  <span style={{ color: '#9ca3af' }}>No specific access</span>
                )}
              </td>
              <td style={{ padding: '12px 8px', verticalAlign: 'top', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ffffff' }}>
                {code.description || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CreateAccessCodeForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    codeType: 'all',
    duration: '30_days',
    quantity: 1,
    maxUsesPerCode: '',
    description: '',
    notes: '',
    assistantIds: [],
    permissions: '{}'
  });
  
  const [{ data, fetching, error }, generateCodes] = useGlobalAction(api.generateBatchAccessCodes);
  
  // Fetch assistants for the multi-select dropdown
  const [{ data: assistants, fetching: assistantsFetching }] = useFindMany(api.assistant, {
    select: {
      id: true,
      name: true,
      description: true
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const params = {
      codeType: formData.codeType,
      duration: formData.duration,
      quantity: parseInt(formData.quantity),
      description: formData.description || undefined,
      notes: formData.notes || undefined,
      assistantIds: formData.assistantIds,
      permissions: formData.permissions ? JSON.parse(formData.permissions) : {}
    };

    if (formData.maxUsesPerCode) {
      params.maxUsesPerCode = parseInt(formData.maxUsesPerCode);
    }

    await generateCodes(params);
    
    if (data && !error) {
      onSuccess();
      setFormData({
        codeType: 'all',
        duration: '30_days',
        quantity: 1,
        maxUsesPerCode: '',
        description: '',
        notes: '',
        assistantIds: [],
        permissions: '{}'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? value : value
    }));
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #374151',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#ffffff',
    backgroundColor: '#1f2937'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#ffffff'
  };

  const formGroupStyle = {
    flex: 1,
    minWidth: '200px'
  };

  const formRowStyle = {
    display: 'flex',
    gap: '15px',
    marginBottom: '15px',
    flexWrap: 'wrap'
  };

  return (
    <div style={{ background: '#1f2937', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#ffffff' }}>Create New Access Codes</h3>
      <form onSubmit={handleSubmit}>
        <div style={formRowStyle}>
          <div style={formGroupStyle}>
            <label htmlFor="codeType" style={labelStyle}>Code Type</label>
            <select
              id="codeType"
              name="codeType"
              value={formData.codeType}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="all">All Assistants</option>
              <option value="specific">Specific Assistants</option>
            </select>
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="duration" style={labelStyle}>Duration</label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="30_days">30 Days</option>
              <option value="90_days">90 Days</option>
              <option value="1_year">1 Year</option>
              <option value="unlimited">Unlimited</option>
            </select>
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="quantity" style={labelStyle}>Quantity</label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="1"
              max="100"
              required
              style={inputStyle}
            />
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="maxUsesPerCode" style={labelStyle}>Max Uses (optional)</label>
            <input
              type="number"
              id="maxUsesPerCode"
              name="maxUsesPerCode"
              value={formData.maxUsesPerCode}
              onChange={handleChange}
              min="1"
              placeholder="Unlimited"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={{ flexBasis: '100%' }}>
            <label htmlFor="assistantIds" style={labelStyle}>Assistant Access</label>
            <select
              id="assistantIds"
              name="assistantIds"
              multiple
              value={formData.assistantIds}
              onChange={(e) => {
                const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                setFormData(prev => ({ ...prev, assistantIds: selectedIds }));
              }}
              style={{ ...inputStyle, minHeight: '100px' }}
            >
              {assistantsFetching ? (
                <option disabled>Loading assistants...</option>
              ) : (
                assistants?.map(assistant => (
                  <option key={assistant.id} value={assistant.id}>
                    {assistant.name} {assistant.description ? `- ${assistant.description}` : ''}
                  </option>
                ))
              )}
            </select>
            <small style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
              Hold Ctrl/Cmd to select multiple assistants. Leave empty for no specific assistant access.
            </small>
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={{ flexBasis: '100%' }}>
            <label htmlFor="permissions" style={labelStyle}>Custom Permissions (JSON)</label>
            <textarea
              id="permissions"
              name="permissions"
              value={formData.permissions}
              onChange={handleChange}
              placeholder='{"feature1": true, "feature2": false}'
              rows="4"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <small style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
              Enter custom permissions as JSON. Example: {`{"feature1": true, "maxTokens": 1000}`}
            </small>
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={{ flexBasis: '100%' }}>
            <label htmlFor="description" style={labelStyle}>Description</label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of the access codes"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={{ flexBasis: '100%' }}>
            <label htmlFor="notes" style={labelStyle}>Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes or usage instructions"
              rows="3"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>

        {error && (
          <div style={{ background: '#7f1d1d', color: '#fed7d7', padding: '12px', borderRadius: '4px', margin: '15px 0' }}>
            Error: {error.toString()}
          </div>
        )}

        {data && (
          <div style={{ background: '#065f46', color: '#d1fae5', padding: '12px', borderRadius: '4px', margin: '15px 0' }}>
            Successfully generated {data.batchSize} access codes!
            <div style={{ marginTop: '10px' }}>
              <strong>Generated Codes:</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '10px 0' }}>
                {data.codes.map(code => (
                  <span key={code} style={{ background: '#1f2937', padding: '4px 8px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>
                    {code}
                  </span>
                ))}
              </div>
              {data.csvData && (
                <button 
                  type="button"
                  style={{
                    background: '#059669',
                    color: '#ffffff',
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                  onClick={() => {
                    const blob = new Blob([data.csvData], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'access-codes.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download CSV
                </button>
              )}
            </div>
          </div>
        )}

        <button 
          type="submit" 
          disabled={fetching} 
          style={{
            background: fetching ? '#374151' : '#3b82f6',
            color: '#ffffff',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '4px',
            cursor: fetching ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            transition: 'background-color 0.2s'
          }}
        >
          {fetching ? 'Generating...' : `Generate ${formData.quantity} Code${formData.quantity > 1 ? 's' : ''}`}
        </button>
      </form>
    </div>
  );
};

export default function AdminAccessCodes() {
  const navigate = useNavigate();
  
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('Descending');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Build filter conditions
  const buildFilter = () => {
    const filters = [];
    
    if (filterStatus === 'active') {
      filters.push({ isActive: { equals: true } });
      filters.push({ 
        OR: [
          { expiresAt: { isSet: false } },
          { expiresAt: { greaterThan: new Date().toISOString() } }
        ]
      });
    } else if (filterStatus === 'expired') {
      filters.push({ expiresAt: { lessThan: new Date().toISOString() } });
    } else if (filterStatus === 'exhausted') {
      // This would need a computed filter, for now just show all
    } else if (filterStatus === 'inactive') {
      filters.push({ isActive: { equals: false } });
    }

    return filters.length > 0 ? { AND: filters } : undefined;
  };

  const [{ data: accessCodes, fetching, error }, refetch] = useFindMany(api.accessCode, {
    select: {
      id: true,
      code: true,
      codeType: true,
      currentUses: true,
      maxUses: true,
      duration: true,
      isActive: true,
      expiresAt: true,
      createdAt: true,
      description: true,
      notes: true,
      permissions: true,
      createdBy: {
        email: true
      }
    },
    filter: buildFilter(),
    sort: { [sortField]: sortDirection },
    search: searchQuery || undefined,
    first: 50
  });

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetch();
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    handleRefresh();
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'Ascending' ? 'Descending' : 'Ascending');
    } else {
      setSortField(field);
      setSortDirection('Descending');
    }
    handleRefresh();
  };

  const exportToCsv = () => {
    if (!accessCodes || accessCodes.length === 0) return;
    
    const headers = ['Code', 'Code Type', 'Current Uses', 'Max Uses', 'Duration', 'Status', 'Expires At', 'Created At', 'Description'];
    const csvContent = [
      headers.join(','),
      ...accessCodes.map(code => [
        code.code,
        code.codeType,
        code.currentUses,
        code.maxUses || 'Unlimited',
        code.duration,
        code.isActive ? 'Active' : 'Inactive',
        code.expiresAt || 'Never',
        code.createdAt,
        (code.description || '').replace(/,/g, ';')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-codes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-access-codes">


        <div className="page-header">
          <div className="header-left">
            <Link 
              to="/admin" 
              className="back-btn"
            >
              ← Back to Admin
            </Link>
            <h1>Access Code Management</h1>
          </div>
          <div className="header-actions">
            <button onClick={handleRefresh} className="refresh-btn">
              🔄 Refresh
            </button>
            <button onClick={exportToCsv} className="export-btn">
              📊 Export CSV
            </button>
          </div>
        </div>

        <CreateAccessCodeForm onSuccess={handleRefresh} />

        <div className="codes-section">
          <div className="section-header">
            <h2>Access Codes</h2>
            <div className="codes-stats">
              {accessCodes && (
                <span>Showing {accessCodes.length} codes</span>
              )}
            </div>
          </div>

          <div className="filter-controls">
            <div className="search-group">
              <input
                type="text"
                placeholder="Search codes or descriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <button 
                onClick={handleRefresh}
                className="search-btn"
              >
                🔍
              </button>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={filterStatus} 
                onChange={(e) => handleFilterChange(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="exhausted">Exhausted</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="sort-group">
              <label>Sort by:</label>
              <select 
                value={sortField}
                onChange={(e) => handleSortChange(e.target.value)}
                className="sort-select"
              >
                <option value="createdAt">Created Date</option>
                <option value="currentUses">Usage Count</option>
                <option value="codeType">Code Type</option>
                <option value="expiresAt">Expiration</option>
                <option value="code">Code</option>
              </select>
              <button 
                onClick={() => setSortDirection(prev => prev === 'Ascending' ? 'Descending' : 'Ascending')}
                className="sort-direction-btn"
              >
                {sortDirection === 'Ascending' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {fetching && <div className="loading">Loading access codes...</div>}
          {error && <div className="error">Error loading access codes: {error.toString()}</div>}
          
          {accessCodes && (
            <AccessCodeTable 
              accessCodes={accessCodes} 
              onRefresh={handleRefresh}
            />
          )}

          {accessCodes && accessCodes.length === 0 && !fetching && (
            <div className="empty-state">
              No access codes found. Create some using the form above.
            </div>
          )}
        </div>

        <style jsx>{`
          .admin-access-codes {
            padding: 20px;
            max-width: 1400px;
            margin: 0 auto;
            color: #333;
          }

          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 15px;
          }

          .header-left {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .back-btn {
            background: #374151;
            color: #ffffff;
            padding: 8px 16px;
            border-radius: 6px;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
            border: 1px solid #4b5563;
            transition: all 0.2s ease;
          }

          .back-btn:hover {
            background: #4b5563;
            border-color: #6b7280;
          }

          .page-header h1 {
            margin: 0;
            color: #333;
          }

          .header-actions {
            display: flex;
            gap: 10px;
          }

          .refresh-btn, .export-btn {
            padding: 8px 16px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 4px;
            cursor: pointer;
            transition: background-color 0.2s;
            color: #333;
          }

          .refresh-btn:hover, .export-btn:hover {
            background: #f5f5f5;
          }

          .form-section {
            background: #f9f9f9;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
          }

          .form-section h3 {
            margin: 0 0 20px 0;
            color: #333;
          }

          .access-code-form .form-row {
            display: flex;
            gap: 15px;
            margin-bottom: 15px;
            flex-wrap: wrap;
          }

          .form-group {
            flex: 1;
            min-width: 200px;
          }

          .form-group.full-width {
            flex-basis: 100%;
          }

          .form-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
            color: #333;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
            background-color: #fff;
          }

          .form-group textarea {
            resize: vertical;
          }

          .form-group small {
            color: #666;
          }

          .submit-btn {
            background: #007bff;
            color: white;
            padding: 12px 24px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            transition: background-color 0.2s;
          }

          .submit-btn:hover:not(:disabled) {
            background: #0056b3;
          }

          .submit-btn:disabled {
            background: #ccc;
            cursor: not-allowed;
          }

          .error-message {
            background: #f8d7da;
            color: #721c24;
            padding: 12px;
            border-radius: 4px;
            margin: 15px 0;
          }

          .success-message {
            background: #d4edda;
            color: #155724;
            padding: 12px;
            border-radius: 4px;
            margin: 15px 0;
          }

          .generated-codes {
            margin-top: 10px;
          }

          .codes-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin: 10px 0;
          }

          .generated-code {
            background: #e9ecef;
            padding: 4px 8px;
            border-radius: 3px;
            font-family: monospace;
            font-size: 12px;
          }

          .download-btn {
            background: #28a745;
            color: #fff;
            padding: 6px 12px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            margin-top: 10px;
          }

          .download-btn:hover {
            background: #218838;
          }

          .codes-section {
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            background: #f8f9fa;
            border-bottom: 1px solid #e0e0e0;
          }

          .section-header h2 {
            margin: 0;
            color: #333;
          }

          .codes-stats {
            color: #666;
            font-size: 14px;
          }

          .filter-controls {
            display: flex;
            gap: 20px;
            padding: 15px 20px;
            background: #f8f9fa;
            border-bottom: 1px solid #e0e0e0;
            flex-wrap: wrap;
            align-items: center;
          }

          .search-group {
            display: flex;
            flex: 1;
            min-width: 300px;
          }

          .search-input {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-right: none;
            border-radius: 4px 0 0 4px;
            color: #333;
            background-color: #fff;
          }

          .search-btn {
            padding: 8px 12px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 0 4px 4px 0;
            cursor: pointer;
            color: #333;
          }

          .filter-group,
          .sort-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .filter-group label,
          .sort-group label {
            font-weight: bold;
            color: #333;
          }

          .filter-select,
          .sort-select {
            padding: 6px 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            color: #333;
            background-color: #fff;
          }

          .sort-direction-btn {
            padding: 6px 10px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
            color: #333;
          }

          .table-container {
            overflow-x: auto;
          }

          .table {
            width: 100%;
            border-collapse: collapse;
          }

          .table th {
            background: #f8f9fa;
            padding: 12px 8px;
            text-align: left;
            font-weight: bold;
            color: #333;
            border-bottom: 2px solid #dee2e6;
          }

          .table td {
            padding: 12px 8px;
            border-bottom: 1px solid #dee2e6;
            vertical-align: top;
            color: #333;
          }

          .table tr:hover {
            background: #f8f9fa;
          }

          .code-cell {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .code-text {
            font-family: monospace;
            font-weight: bold;
            font-size: 14px;
          }

          .copy-btn {
            background: #fff;
            border: 1px solid #ddd;
            padding: 4px 6px;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            color: #333;
          }

          .copy-btn:hover {
            background: #f5f5f5;
          }

          .code-type {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
            text-transform: capitalize;
          }

          .code-type.trial {
            background: #e3f2fd;
            color: #1565c0;
          }

          .code-type.full_access {
            background: #e8f5e8;
            color: #2e7d32;
          }

          .code-type.limited {
            background: #fff3cd;
            color: #856404;
          }

          .code-type.custom {
            background: #f3e5f5;
            color: #7b1fa2;
          }

          .badge {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
          }

          .badge-success {
            background: #d4edda;
            color: #155724;
          }

          .badge-warning {
            background: #fff3cd;
            color: #856404;
          }

          .badge-danger {
            background: #f8d7da;
            color: #721c24;
          }

          .badge-secondary {
            background: #e2e3e5;
            color: #383d41;
          }

          .description-cell {
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .assistant-access-cell {
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .loading {
            text-align: center;
            padding: 40px;
            color: #333;
          }

          .error {
            background: #f8d7da;
            color: #721c24;
            padding: 20px;
            margin: 20px;
            border-radius: 4px;
          }

          .empty-state {
            text-align: center;
            padding: 40px;
            color: #333;
          }

          @media (max-width: 768px) {
            .admin-access-codes {
              padding: 10px;
            }

            .page-header {
              flex-direction: column;
              gap: 15px;
              align-items: flex-start;
            }

            .header-left {
              flex-direction: column;
              align-items: flex-start;
              gap: 10px;
            }

            .filter-controls {
              flex-direction: column;
              gap: 10px;
              align-items: stretch;
            }

            .search-group {
              min-width: auto;
            }

            .access-code-form .form-row {
              flex-direction: column;
            }

            .form-group {
              min-width: auto;
            }
          }
        `}</style>
    </div>
  );
}